import { useState, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { sqlRead } from "../../utils/sqlClient";
import { authHeader } from "../../utils/apiToken";
import { BID_PACKAGE_REVIEW_URL } from "../../config/api";
import AssistantMarkdown from "./assistants/AssistantMarkdown";

// AI Bid Document Summary — reads every PDF in the Bid Package's SharePoint
// folder (cs-est-takeoff-api/bid-package-review, db/41_bid_package_review.sql)
// and shows the organized result: scope, key dates, bonding/insurance/wage
// requirements, special requirements/exclusions, and open questions for the
// estimator. Same 202-kickoff + status-poll shape as the Estimating
// Assistant's contract review (ReviewAssistants.jsx), but persisted per
// project instead of ephemeral per browser session — the last result loads
// automatically, no need to re-run on every visit.

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const POLL_INTERVAL_MS = 4000;
const MAX_POLLS = 225; // ~15 min ceiling
const MAX_POLLS_WITHOUT_JOB = 30; // ~2 min — the async kickoff can be lost

function fmtDateTime(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt)) return "";
  return dt.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function BidPackageAISummary({ projectId, folderUrl }) {
  const { accounts } = useMsal();
  const account = accounts[0];

  const [loading, setLoading] = useState(true);
  const [result, setResult]   = useState(null); // { text, documentCount, skipped, completedAt, submittedBy }
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError]     = useState(null);
  const [showSkipped, setShowSkipped] = useState(false);

  const cancelledRef = useRef(false);
  useEffect(() => () => { cancelledRef.current = true; }, []);

  const loadResult = async () => {
    const rows = await sqlRead(`EXEC sp_GetBidPackageReviewResult @project_id=${projectId}`);
    if (!rows?.length) { setResult(null); return; }
    const text = rows.map(r => r.PartText || "").join("\n\n").trim();
    let skipped = [];
    try { skipped = JSON.parse(rows[0].SkippedJson || "[]"); } catch { /* ignore malformed */ }
    setResult({
      text,
      documentCount: rows[0].DocumentCount,
      skipped,
      completedAt: rows[0].CompletedAt,
      submittedBy: rows[0].SubmittedBy,
    });
  };

  useEffect(() => {
    setLoading(true);
    loadResult().catch(e => setError(e.message)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const run = async () => {
    if (!folderUrl) return;
    cancelledRef.current = false;
    setRunning(true); setError(null); setProgress("Reading the SharePoint folder…");
    try {
      if (!BID_PACKAGE_REVIEW_URL) throw new Error("This feature is not configured (missing VITE_BID_PACKAGE_REVIEW_URL).");
      const jobId = (crypto.randomUUID && crypto.randomUUID()) || `job-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const res = await fetch(BID_PACKAGE_REVIEW_URL, {
        method:  "POST",
        headers: { "Content-Type": "application/json", ...(await authHeader()) },
        body: JSON.stringify({
          job_id: jobId, project_id: projectId, folder_url: folderUrl,
          submitted_by: account?.name || account?.username || "",
        }),
      });
      if (!res.ok && res.status !== 202) {
        const t = await res.text().catch(() => "");
        throw new Error(`Could not start the review (${res.status})${t ? `: ${t.slice(0, 200)}` : ""}`);
      }

      setProgress("Analyzing the bid documents…");
      const started = Date.now();
      let done = false;
      let jobSeen = false;
      for (let i = 0; i < MAX_POLLS && !cancelledRef.current; i++) {
        await sleep(POLL_INTERVAL_MS);
        if (cancelledRef.current) return;
        let rows;
        try {
          rows = await sqlRead(`EXEC sp_GetBidPackageReviewStatus @job_id='${jobId}'`);
        } catch {
          continue; // transient read blip — keep polling
        }
        const st = rows?.[0];
        if (!st) {
          if (!jobSeen && i >= MAX_POLLS_WITHOUT_JOB) {
            throw new Error("The review never started (the service didn't pick up the job). Try again.");
          }
          continue;
        }
        jobSeen = true;
        if (st.Status === "Failed") throw new Error(st.ErrorMessage || "The review failed. Try again.");
        if (st.Status === "Complete") { done = true; break; }
        setProgress(`Analyzing the bid documents… (${Math.round((Date.now() - started) / 1000)}s)`);
      }
      if (cancelledRef.current) return;
      if (!done) throw new Error("The review is taking longer than expected. Try again in a few minutes.");

      await loadResult();
    } catch (e) {
      if (!cancelledRef.current) setError(e.message || String(e));
    } finally {
      if (!cancelledRef.current) { setRunning(false); setProgress(""); }
    }
  };

  const cardStyle = { background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "16px 18px", marginBottom: 16 };

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.08em", display: "flex", alignItems: "center", gap: 6 }}>
          <span>🪄</span>AI Bid Document Summary
        </div>
        <button
          onClick={run}
          disabled={running || !folderUrl}
          title={!folderUrl ? "Add a SharePoint folder URL above first" : undefined}
          style={{
            padding: "6px 14px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
            background: running || !folderUrl ? "var(--cs-border)" : "var(--cs-navy)",
            color: running || !folderUrl ? "var(--text3)" : "#fff",
            border: "none", borderRadius: "var(--radius)",
            cursor: running || !folderUrl ? "default" : "pointer",
          }}
        >
          {running ? "Analyzing…" : result ? "↻ Re-analyze" : "Analyze bid documents →"}
        </button>
      </div>

      {!folderUrl && (
        <div style={{ fontSize: 12, color: "var(--text3)" }}>Add a SharePoint folder URL above to let AI read through the bid documents.</div>
      )}

      {running && (
        <div style={{ fontSize: 12, color: "var(--text2)", display: "flex", alignItems: "center", gap: 8 }}>
          <span className="spinner spinner-dark" />{progress || "Working…"}
          <span style={{ color: "var(--text3)" }}>— large folders can take a few minutes; you can leave this tab open.</span>
        </div>
      )}

      {error && (
        <div style={{ fontSize: 12, color: "var(--red)", padding: "8px 12px", background: "var(--red-lt)", borderRadius: "var(--radius)", marginTop: running ? 10 : 0 }}>
          {error}
        </div>
      )}

      {!running && loading && <div style={{ fontSize: 12, color: "var(--text3)" }}>Loading…</div>}

      {!running && !loading && result && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 10 }}>
            Last analyzed {fmtDateTime(result.completedAt)}{result.submittedBy ? ` by ${result.submittedBy}` : ""}
            {result.documentCount ? ` · ${result.documentCount} document${result.documentCount === 1 ? "" : "s"}` : ""}
            {result.skipped?.length > 0 && (
              <>
                {" · "}
                <button onClick={() => setShowSkipped(s => !s)} style={{ background: "none", border: "none", padding: 0, font: "inherit", color: "var(--cs-blue)", cursor: "pointer" }}>
                  {result.skipped.length} skipped {showSkipped ? "▲" : "▼"}
                </button>
              </>
            )}
          </div>
          {showSkipped && result.skipped?.length > 0 && (
            <div style={{ fontSize: 11, color: "var(--text3)", background: "var(--surface2)", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "8px 10px", marginBottom: 10 }}>
              {result.skipped.map((s, i) => (
                <div key={i} style={{ padding: "2px 0" }}>
                  <span style={{ fontFamily: "monospace" }}>{s.name}</span> — {s.reason}
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 13 }}>
            <AssistantMarkdown text={result.text} />
          </div>
        </div>
      )}

      {!running && !loading && !result && folderUrl && !error && (
        <div style={{ fontSize: 12, color: "var(--text3)" }}>Not analyzed yet — click "Analyze bid documents" to read through the folder.</div>
      )}
    </div>
  );
}
